<template>
	<v-dialog
		:value="visible"
		fullscreen
		hide-overlay
		transition="dialog-bottom-transition"
		@input="handleVisibilityChange"
	>
		<div class="chart-detail-overlay">
			<!-- Minimal Header -->
			<div class="overlay-header">
				<div class="overlay-header-left">
					<v-img
						:src="logoSrc"
						max-width="100"
						class="overlay-logo"
					/>
					<h2 class="overlay-title">
						{{ title || (chart && chart.title) || 'Chart Details' }}
					</h2>
				</div>
				<div class="overlay-actions">
					<v-btn
						color="primary"
						:disabled="!hasData"
						class="text-none"
						@click="exportCSV"
					>
						<v-icon left>
							mdi-download
						</v-icon>
						Export CSV
					</v-btn>
					<v-btn
						icon
						title="Close"
						@click="closeOverlay"
					>
						<v-icon>
							mdi-close
						</v-icon>
					</v-btn>
				</div>
			</div>

			<!-- Filter Row - matches dashboard filter accordion styling -->
			<div
				v-if="filters"
				class="overlay-filter-row"
			>
				<!-- Date Range Selector -->
				<div class="filter-field-col">
					<DateRangeSelector
						:start-date="filters.startDate"
						:end-date="filters.endDate"
						:period="filters.periodValue"
						button-class="date-range-btn"
						@apply="onDateRangeApply"
					/>
				</div>
				<!-- Brand Filter -->
				<div
					v-if="showBrandFilter"
					class="filter-field-col"
				>
					<Dropdown
						v-model="currentBrandId"
						label="Brand"
						:items="brandItems"
						item-text="text"
						item-value="value"
						outlined
						dense
						hide-details
					/>
				</div>
			</div>

			<!-- Charts Section -->
			<div class="charts-container">
				<!-- Loading state while charts prepare -->
				<div
					v-if="hasData && !chartsReady"
					class="charts-row"
				>
					<div class="chart-panel bar-chart-panel">
						<v-skeleton-loader
							type="image"
							height="380"
						/>
					</div>
					<div class="distribution-panel">
						<div class="distribution-content">
							<v-skeleton-loader
								type="image"
								width="280"
								height="280"
							/>
							<v-skeleton-loader
								type="list-item@6"
								width="300"
							/>
						</div>
					</div>
				</div>

				<div
					v-if="hasData && chartsReady"
					class="charts-row"
				>
					<!-- Bar Chart Panel -->
					<div class="chart-panel bar-chart-panel">
						<div class="chart-wrapper">
							<BarChart
								:chart-data="filteredBarData"
								:chart-options="barChartOptions"
								:palette="palette"
								:color-version="colorVersion"
								:height="chartHeight"
							/>
						</div>
					</div>

					<!-- Pie Chart + Legend Panel -->
					<div class="distribution-panel">
						<div class="distribution-content">
							<!-- Pie Chart -->
							<div class="pie-wrapper">
								<PieChart
									:chart-data="pieChartData"
									:chart-options="pieChartOptions"
									:height="pieChartHeight"
								/>
							</div>
							<!-- Legend -->
							<div class="legend-wrapper">
								<div class="legend-items">
									<div
										v-for="(item, index) in legendItems"
										:key="`legend-${index}`"
										class="legend-item"
										:class="{ 'legend-item-hidden': item.hidden }"
										@click="toggleSeries(item.label)"
									>
										<span
											class="legend-color"
											:style="{ backgroundColor: item.color }"
										/>
										<span
											class="legend-label"
											:class="{ 'text-line-through': item.hidden }"
										>
											{{ item.label }}
										</span>
										<span class="legend-value">
											{{ item.percentage }}%
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Data Table Section -->
				<div
					v-if="hasData && chartsReady"
					class="table-section"
				>
					<ChartDataTable
						:data="tableData"
						:series-names="visibleSeriesNames"
						:colors="visibleColors"
						:max-height="400"
					/>
				</div>
			</div>

			<!-- No Data Message -->
			<div
				v-if="!hasData"
				class="no-data-message"
			>
				<v-icon
					size="64"
					color="grey"
				>
					mdi-chart-box-outline
				</v-icon>
				<p class="mt-3">
					No chart data available
				</p>
			</div>
		</div>
	</v-dialog>
</template>

<script>
/* eslint-disable max-lines */
import BarChart from '../../../../../shared/components/chart/bar-chart.vue'
import PieChart from '../../../../../shared/components/chart/pie-chart.vue'
import ChartDataTable from './chart-data-table.vue'
import DateRangeSelector from '../../../../../shared/components/date-range-selector.vue'
import Dropdown from '../../../../../shared/components/dropdown.vue'
import { brandOptions } from '../../../../../shared/state/brands.js'
import { derivePieData, toPieChartFormat } from '../../../../../shared/utils/derive-pie-data.js'
import { getChartColorByIndex } from '../../../../../shared/utils/getChartColorByIndex.js'
import { transformTimeSeriesForExport, exportToCSV } from '../../../../../shared/utils/csv-export.js'
import logoLight from '../../../../../shared/assets/Syrenis-Black.svg'
import logoDark from '../../../../../shared/assets/Syrenis-red.svg'

export default {
	name: 'chart-detail-overlay',

	components: {
		BarChart,
		PieChart,
		ChartDataTable,
		DateRangeSelector,
		Dropdown
	},

	props: {
		visible: {
			type: Boolean,
			default: false
		},
		chart: {
			type: Object,
			default: null
		},
		title: {
			type: String,
			default: ''
		},
		palette: {
			type: String,
			default: 'default'
		},
		colorVersion: {
			type: Number,
			default: 0
		},
		filters: {
			type: Object,
			default: null
		},
		availableBrands: {
			type: Array,
			default: () => []
		}
	},

	data () {
		return {
			hiddenSeries: [],
			chartHeight: 380,
			pieChartHeight: 220,
			chartsReady: false
		}
	},

	computed: {
		/**
		 * Logo source based on dark mode
		 */
		logoSrc () {
			return this.$vuetify.theme.dark ? logoDark : logoLight
		},

		/**
		 * Check if chart data exists
		 */
		hasData () {
			return (
				this.chart &&
        this.chart.chartData &&
        this.chart.chartData.datasets &&
        this.chart.chartData.datasets.length > 0
			)
		},

		/**
		 * Show brand filter if there are multiple brands
		 */
		showBrandFilter () {
			return brandOptions.value && brandOptions.value.length >= 2
		},

		/**
		 * Brand dropdown items - "All" first, then alphabetically sorted
		 */
		brandItems () {
			if (!brandOptions.value) return []
			const items = [...brandOptions.value]
			return items.sort((a, b) => {
				const aText = (a.text || '').toLowerCase()
				const bText = (b.text || '').toLowerCase()
				// "All" or "View All" always first
				if (aText === 'all' || aText === 'view all') return -1
				if (bText === 'all' || bText === 'view all') return 1
				// Alphabetical sort for everything else (including Universal)
				return (a.text || '').localeCompare(b.text || '')
			})
		},

		/**
		 * Current brand ID for dropdown v-model
		 */
		currentBrandId: {
			get () {
				return this.filters ? this.filters.brandId : null
			},
			set (value) {
				this.onBrandChange(value)
			}
		},

		/**
     * Extract series names from chart data
     */
		seriesNames () {
			if (!this.hasData) return []
			return this.chart.chartData.datasets.map(dataset => dataset.label || 'Unnamed')
		},

		/**
     * Generate colors for each series
     * References colorVersion to trigger reactivity when custom colours change
     */
		colors () {
			// Reference colorVersion to trigger reactivity when colours are updated
			// eslint-disable-next-line no-unused-vars
			const _version = this.colorVersion

			return this.seriesNames.map((_, index) => getChartColorByIndex(index, this.palette))
		},

		/**
     * Filter out hidden series from bar chart data
     */
		filteredBarData () {
			if (!this.hasData) return null

			const filteredDatasets = this.chart.chartData.datasets.filter(dataset => {
				return !this.hiddenSeries.includes(dataset.label)
			})

			return {
				...this.chart.chartData,
				datasets: filteredDatasets
			}
		},

		/**
     * Derive pie data from chart data
     */
		pieData () {
			if (!this.hasData) return []

			// Use the filtered data for pie chart
			const dataToUse = this.filteredBarData

			return derivePieData(dataToUse, this.colors, {
				threshold: 0,
				maxItems: 999
			})
		},

		/**
     * Convert pie data to Chart.js format
     */
		pieChartData () {
			return toPieChartFormat(this.pieData)
		},

		/**
     * Generate legend items with visibility state, sorted by descending value
     */
		legendItems () {
			if (!this.hasData) return []

			// Calculate totals for percentages
			const totals = this.seriesNames.map((seriesName, index) => {
				const dataset = this.chart.chartData.datasets[index]
				if (!dataset || !dataset.data) return 0

				return dataset.data.reduce((sum, value) => sum + (value || 0), 0)
			})

			const grandTotal = totals.reduce((sum, val) => sum + val, 0)

			const items = this.seriesNames.map((seriesName, index) => {
				const value = totals[index]
				const percentage = grandTotal > 0 ? ((value / grandTotal) * 100).toFixed(1) : 0

				return {
					label: seriesName,
					color: this.colors[index],
					value,
					percentage,
					hidden: this.hiddenSeries.includes(seriesName)
				}
			})

			// Sort by descending value
			return items.sort((a, b) => b.value - a.value)
		},

		/**
     * Get visible series names (for table display)
     */
		visibleSeriesNames () {
			return this.seriesNames.filter(name => !this.hiddenSeries.includes(name))
		},

		/**
     * Get colors for visible series only
     */
		visibleColors () {
			return this.colors.filter((_, index) => {
				const seriesName = this.seriesNames[index]
				return !this.hiddenSeries.includes(seriesName)
			})
		},

		/**
     * Transform chart data for table display
     */
		tableData () {
			if (!this.hasData) return []

			const labels = this.chart.chartData.labels || []
			const datasets = this.chart.chartData.datasets || []

			// Build rows with date and series values
			return labels.map((label, labelIndex) => {
				const row = { date: label }

				datasets.forEach(dataset => {
					if (!this.hiddenSeries.includes(dataset.label)) {
						row[dataset.label] = dataset.data[labelIndex] || 0
					}
				})

				return row
			})
		},

		/**
     * Bar chart options
     */
		barChartOptions () {
			return {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						display: false // Legend is in the custom panel
					},
					tooltip: {
						mode: 'index',
						intersect: false
					}
				},
				scales: {
					x: {
						stacked: true
					},
					y: {
						stacked: true,
						beginAtZero: true
					}
				}
			}
		},

		/**
     * Pie chart options
     */
		pieChartOptions () {
			return {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						display: false // Legend is in the custom panel
					},
					tooltip: {
						callbacks: {
							label: context => {
								const label = context.label || ''
								const value = context.parsed || 0
								const dataset = context.dataset
								const total = dataset.data.reduce((acc, val) => acc + val, 0)
								const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0
								return `${label}: ${value.toLocaleString()} (${percentage}%)`
							}
						}
					}
				}
			}
		}
	},

	watch: {
		visible (newVal) {
			if (newVal) {
				// Reset hidden series when overlay opens
				this.hiddenSeries = []
				// Reset chartsReady to delay chart rendering until container is sized
				this.chartsReady = false

				// Delay chart rendering until after dialog transition completes
				// The dialog-bottom-transition takes ~300ms, so we wait for:
				// 1. $nextTick (DOM update)
				// 2. Transition to complete (~350ms)
				// 3. Then show charts and trigger resize
				this.$nextTick(() => {
					setTimeout(() => {
						this.chartsReady = true
						// Additional resize after charts mount
						this.$nextTick(() => {
							window.dispatchEvent(new Event('resize'))
						})
					}, 350)
				})
			} else {
				this.chartsReady = false
			}
		}
	},

	methods: {
		/**
     * Handle visibility change
     */
		handleVisibilityChange (value) {
			this.$emit('update:visible', value)
			if (!value) {
				this.closeOverlay()
			}
		},

		/**
     * Close the overlay
     */
		closeOverlay () {
			this.$emit('close')
			this.$emit('update:visible', false)
		},

		/**
		 * Handle date range apply from selector
		 */
		onDateRangeApply ({ period, startDate, endDate }) {
			this.emitFilterChange({
				...this.filters,
				periodValue: period,
				startDate,
				endDate
			})
		},

		/**
		 * Handle brand change
		 */
		onBrandChange (brandId) {
			this.emitFilterChange({
				...this.filters,
				brandId
			})
		},

		/**
		 * Emit filter change to parent
		 */
		emitFilterChange (updatedFilters) {
			this.$emit('filter-change', updatedFilters)
		},

		/**
     * Toggle series visibility
     */
		toggleSeries (seriesName) {
			const index = this.hiddenSeries.indexOf(seriesName)
			if (index > -1) {
				this.hiddenSeries.splice(index, 1)
			} else {
				this.hiddenSeries.push(seriesName)
			}
		},

		/**
     * Export data to CSV
     */
		exportCSV () {
			if (!this.hasData) {
				console.warn('No data to export')
				return
			}

			try {
				// Get visible series names for export
				const seriesNames = this.visibleSeriesNames

				// Transform data for export
				const exportData = transformTimeSeriesForExport(this.tableData, seriesNames)

				// Generate columns
				const columns = ['Date', ...seriesNames, 'Total']

				// Generate filename from title
				const timestamp = new Date().toISOString().split('T')[0]
				const baseFilename = this.title || this.chart.title || 'chart-data'
				const sanitizedFilename = baseFilename.replace(/[^a-z0-9]/gi, '-').toLowerCase()
				const filename = `${sanitizedFilename}-${timestamp}`

				// Export
				exportToCSV(exportData, columns, filename)
			} catch (error) {
				console.error('Error exporting CSV:', error)
				// Could show a toast notification here
			}
		}
	}
}
</script>

<style scoped>
.chart-detail-overlay {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--global-background, #f5f5f5);
  overflow: hidden;
}

/* Minimal Header */
.overlay-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: var(--global-card-background, #ffffff);
  border-bottom: 1px solid var(--global-border, #e0e0e0);
  flex-shrink: 0;
}

/* Filter Row */
.overlay-filter-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background-color: var(--global-card-background, #ffffff);
  border-bottom: 1px solid var(--global-border-color, #e0e0e0);
  flex-shrink: 0;
  flex-wrap: wrap;
}

.filter-field-col {
  min-width: 160px;
}

.filter-field-col .v-input {
  max-width: 200px;
}

.date-range-btn {
  height: 40px;
  border-color: var(--global-border-color, rgba(0, 0, 0, 0.24));
  color: var(--global-foreground, #1f2937);
}

.date-range-btn .v-icon {
  color: var(--global-muted-foreground, #6b7280);
}

.brand-dropdown {
  min-width: 180px;
}

.overlay-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.overlay-logo {
  flex-shrink: 0;
}

.overlay-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--global-foreground, #1f2937);
  margin: 0;
}

.overlay-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Charts Container */
.charts-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  min-height: 0;
}

.charts-row {
  display: flex;
  gap: 24px;
  margin-bottom: 32px;
  flex-shrink: 0;
  align-items: stretch;
  height: 400px;
  width: 100%;
}

/* Bar Chart Panel */
.chart-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.bar-chart-panel {
  flex: 0 0 58%;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.chart-wrapper {
  position: relative;
  flex: 1;
  width: 100%;
  min-height: 0;
  overflow: hidden;
}

/* Distribution Panel (Pie + Legend) */
.distribution-panel {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 300px;
  max-width: 600px;
  min-height: 0;
}

.distribution-content {
  display: flex;
  gap: 16px;
  align-items: stretch;
  flex: 1;
  width: 100%;
  min-height: 0;
}

.pie-wrapper {
  flex: 0 0 auto;
  width: 280px;
  height: 280px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.legend-wrapper {
  width: 300px;
  flex-shrink: 0;
  max-height: 300px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.legend-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-right: 4px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 0;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.legend-item:hover {
  opacity: 0.7;
}

.legend-item-hidden {
  opacity: 0.4;
}

.legend-color {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-label {
  flex: 1;
  font-size: 12px;
  color: var(--global-foreground, #1f2937);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 250px;
}

.text-line-through {
  text-decoration: line-through;
}

.legend-value {
  font-size: 12px;
  color: var(--global-muted-foreground, #6b7280);
  flex-shrink: 0;
  text-align: right;
  min-width: 38px;
}

/* No Data Message */
.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: var(--global-muted-foreground, #6b7280);
  text-align: center;
}

.no-data-message p {
  font-size: 16px;
  margin: 0;
}

/* Table Section */
.table-section {
  background-color: var(--global-card-background, #ffffff);
  border: 1px solid var(--global-border, #e0e0e0);
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
}

.table-header {
  margin-bottom: 12px;
}

.table-header h3 {
  font-size: 14px;
  font-weight: 600;
  color: var(--global-foreground, #1f2937);
  margin: 0;
}

/* Responsive Layout */
@media (max-width: 1400px) {
  .bar-chart-panel {
    max-width: 50%;
  }
}

@media (max-width: 1200px) {
  .charts-row {
    flex-direction: column;
    gap: 24px;
    height: auto;
  }

  .bar-chart-panel {
    flex: none;
    width: 100%;
    max-width: 100%;
    height: 350px;
  }

  .distribution-panel {
    flex: none;
    width: 100%;
    height: 350px;
  }

  .distribution-content {
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .distribution-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .legend-wrapper {
    max-height: 200px;
    width: 100%;
  }

  .pie-wrapper {
    width: 180px;
    height: 180px;
  }
}

/* Dark Mode Support */
.theme--dark .chart-detail-overlay,
.dark .chart-detail-overlay {
  background-color: var(--global-background, #121214);
}

.theme--dark .overlay-header,
.dark .overlay-header {
  background-color: var(--global-card-background, #1c1c1e);
  border-color: var(--global-border, #374151);
}

.theme--dark .overlay-filter-row,
.dark .overlay-filter-row {
  background-color: var(--global-card-background, #1c1c1e);
  border-color: var(--global-border-color, #374151);
}

.theme--dark .date-range-btn,
.dark .date-range-btn {
  border-color: var(--global-border-color, #374151);
  color: var(--global-foreground, #f5f5f5);
}

.theme--dark .date-range-btn .v-icon,
.dark .date-range-btn .v-icon {
  color: var(--global-muted-foreground, #9ca3af);
}

.theme--dark .overlay-title,
.dark .overlay-title {
  color: var(--global-foreground, #f5f5f5);
}

.theme--dark .table-section,
.dark .table-section {
  background-color: var(--global-card-background, #1c1c1e);
  border-color: var(--global-border, #374151);
}

.theme--dark .chart-panel-title,
.dark .chart-panel-title {
  color: var(--global-foreground, #f9fafb);
}

.theme--dark .legend-label,
.dark .legend-label {
  color: var(--global-foreground, #f9fafb);
}

.theme--dark .legend-value,
.dark .legend-value {
  color: var(--global-muted-foreground, #9ca3af);
}

.theme--dark .no-data-message,
.dark .no-data-message {
  color: var(--global-muted-foreground, #9ca3af);
}

/* Scrollbar Styling */
.charts-container::-webkit-scrollbar,
.legend-items::-webkit-scrollbar {
  width: 6px;
}

.charts-container::-webkit-scrollbar-track,
.legend-items::-webkit-scrollbar-track {
  background: transparent;
}

.charts-container::-webkit-scrollbar-thumb,
.legend-items::-webkit-scrollbar-thumb {
  background: var(--global-border, #d1d5db);
  border-radius: 3px;
}

.charts-container::-webkit-scrollbar-thumb:hover,
.legend-items::-webkit-scrollbar-thumb:hover {
  background: var(--global-muted-foreground, #9ca3af);
}

.theme--dark .legend-items::-webkit-scrollbar-thumb,
.dark .legend-items::-webkit-scrollbar-thumb {
  background: var(--global-border, #4b5563);
}

.theme--dark .legend-items::-webkit-scrollbar-thumb:hover,
.dark .legend-items::-webkit-scrollbar-thumb:hover {
  background: var(--global-muted-foreground, #6b7280);
}
</style>
